/**
 * B1069 — Re-vérification groupée des synthèses périmées
 * (Eric 2026-09-04 SPEC STRICK PROD).
 *
 * Chaque vérification appelle un fournisseur PAYANT. Deux garde-fous choisis
 * par Eric : une confirmation qui annonce le nombre EXACT d'appels, et un lot
 * plafonné à 25 synthèses. Le traitement tourne en tâche de fond : on peut
 * quitter la page et revenir, la progression est reprise depuis le job.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowsClockwise } from "@phosphor-icons/react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Progress } from "../ui/progress";

const API = process.env.REACT_APP_BACKEND_URL;
const JOB_KEY = "knoria.reverifyJobId"; // permet de retrouver le job au retour

export const ReverifyStaleButton = ({ staleCount = 0, onDone, oneId = null, label }) => {
  const [preview, setPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const poll = useCallback(
    (jobId) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await axios.get(`${API}/api/agent/jobs/${jobId}`, {
            withCredentials: true,
          });
          setJob(data);
          if (data.status !== "running") {
            stopPolling();
            localStorage.removeItem(JOB_KEY);
            if (data.status === "done") {
              const r = data.result || {};
              toast.success(
                `${r.syntheses_processed || 0} synthèse(s) re-vérifiée(s) · ` +
                  `${r.total_claims_detected || 0} erreur(s) détectée(s)` +
                  (r.remaining_after ? ` · ${r.remaining_after} restante(s)` : "")
              );
            } else {
              toast.error("Re-vérification interrompue : " + (data.error || "erreur inconnue"));
            }
            if (onDone) onDone();
          }
        } catch {
          stopPolling();
        }
      }, 3000);
    },
    [onDone]
  );

  // Reprise au retour sur la page : le lot continue côté serveur.
  useEffect(() => {
    const existing = localStorage.getItem(JOB_KEY);
    if (existing && !oneId) poll(existing);
    return stopPolling;
  }, [poll, oneId]);

  const openConfirm = async () => {
    if (oneId) {
      setPreview({ planned_calls: 1, stale_count: 1, remaining_after: 0 });
      setConfirmOpen(true);
      return;
    }
    try {
      const { data } = await axios.get(
        `${API}/api/admin/synthesis-verifications/reverify-preview`,
        { withCredentials: true }
      );
      setPreview(data);
      setConfirmOpen(true);
    } catch (e) {
      toast.error("Estimation impossible : " + (e?.response?.data?.detail || e.message));
    }
  };

  const launch = async () => {
    setConfirmOpen(false);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/synthesis-verifications/reverify`,
        oneId ? { synthesis_ids: [oneId] } : {},
        { withCredentials: true }
      );
      setJob({ status: "running", chunks_processed: 0, total_chunks: data.count });
      if (!oneId) localStorage.setItem(JOB_KEY, data.job_id);
      poll(data.job_id);
      toast.info(`Re-vérification lancée sur ${data.count} synthèse(s) — ${data.provider}`);
    } catch (e) {
      toast.error("Lancement impossible : " + (e?.response?.data?.detail || e.message));
    }
  };

  const running = job?.status === "running";
  const done = job?.total_chunks ? Math.round((100 * (job.chunks_processed || 0)) / job.total_chunks) : 0;

  return (
    <span className={oneId ? "inline" : "flex flex-col items-end gap-2"}>
      <button
        onClick={openConfirm}
        disabled={running || (!oneId && staleCount === 0)}
        className={
          oneId
            ? "text-xs text-[#002FA7] underline disabled:opacity-40"
            : "inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#002FA7] text-white text-sm font-medium hover:bg-[#00246f] disabled:opacity-50"
        }
        data-testid={oneId ? `reverify-one-${oneId}` : "reverify-all-btn"}
      >
        {!oneId && <ArrowsClockwise size={16} className={running ? "animate-spin" : ""} />}
        {label || (running ? "Re-vérification en cours…" : "Tout re-vérifier")}
      </button>

      {running && !oneId && (
        <div className="w-64" data-testid="reverify-progress">
          <Progress value={done} className="h-2" />
          <p className="text-[11px] text-zinc-500 mt-1">
            {job.chunks_processed || 0}/{job.total_chunks} · {job.current_step || "démarrage…"}
          </p>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="reverify-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Relancer la vérification factuelle ?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Cette action déclenche{" "}
                  <strong data-testid="reverify-planned-calls">
                    {preview?.planned_calls ?? 0} appel(s) facturé(s)
                  </strong>{" "}
                  au fournisseur de vérification
                  {preview?.provider ? ` (${preview.provider})` : ""}.
                </p>
                {preview?.remaining_after ? (
                  <p className="text-amber-700">
                    Le lot est plafonné à {preview.batch_limit} synthèses :{" "}
                    {preview.remaining_after} resteront à traiter, à relancer ensuite.
                  </p>
                ) : null}
                <p className="text-zinc-500">
                  Le traitement continue en arrière-plan : tu peux quitter cette page et
                  revenir, la progression est conservée.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reverify-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={launch} data-testid="reverify-confirm">
              Lancer la re-vérification
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </span>
  );
};

export default ReverifyStaleButton;
